import React, { useState, useEffect, useMemo } from "react";
import { useGetMainSecondOtherContactsQuery } from "../store/apiSlice";
import EmailEditor from "../components/EmailEditor";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setEmployer } from '../store/postsSlice';
import { FixedSizeList as List } from 'react-window';

const schoolAffiliationOptions = [
  "Public",
  "Private",
  "Independent Public School",
  "Independent",
  "Christian",
  "Catholic",
  "Not Assigned" // will map to '' and null
];

const schoolTypeOptions = [
  "TAFE/Vocational",
  "Special Education",
  "Secondary",
  "Primary",
  "Other Workplaces",
  "Kindergarten",
  "K-12",
  "Not Assigned" // will map to '' and null
];


const SalesDatabase = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const [selectedState, setSelectedState] = useState("");
  const [showMain, setShowMain] = useState(true);
  const [showSecondary, setShowSecondary] = useState(true);
  const [showOther, setShowOther] = useState(true);
  const [missingContacts, setMissingContacts] = useState([]);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [isMissingContactsVisible, setIsMissingContactsVisible] = useState(false);
  const [selectedSchoolAffiliations, setSelectedSchoolAffiliations] = useState([]);
  const [selectedSchoolTypes, setSelectedSchoolTypes] = useState([]);

  const stateList = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"];

  const [hrFilter, setHrFilter] = useState(false);
  const [recruiterFilter, setRecruiterFilter] = useState(false);

  const { data, isLoading, isError, refetch } = useGetMainSecondOtherContactsQuery(selectedState, {
    skip: !selectedState,
  });

  const handleStateChange = (event) => {
    const state = event.target.value;
    setSelectedState(state);
  };

  const filteredData = Object.keys(data?.employers || {}).filter((employerId) => {
    const employer = data?.employers[employerId];
  
    const schoolAff = employer?.schoolAffilliation ?? "Not Assigned";
    const schoolTyp = employer?.schoolType ?? "Not Assigned";
  
    const isAffiliationMatch =
      selectedSchoolAffiliations.length === 0 ||
      selectedSchoolAffiliations.includes(
        schoolAff.trim() === "" ? "Not Assigned" : schoolAff
      );
  
    const isTypeMatch =
      selectedSchoolTypes.length === 0 ||
      selectedSchoolTypes.includes(
        schoolTyp.trim() === "" ? "Not Assigned" : schoolTyp
      );
  
  
    return isAffiliationMatch && isTypeMatch;
  });
  

  // Filtered contacts
  const filteredContacts = Object.keys(data?.employers || {}).flatMap((employerId) => {
    const employer = data?.employers[employerId];
    const contacts = [];

    if (showMain && employer?.main_contact?.email) {
      contacts.push(employer.main_contact);
    }
    if (showSecondary && employer?.secondary_contact?.email) {
      contacts.push(employer.secondary_contact);
    }
    if (showOther && employer?.other_contacts?.length > 0) {
      contacts.push(...employer.other_contacts);
    }

    return contacts;
  });



  useEffect(() => {
    if (selectedState) {
      refetch();
    }
  }, [selectedState, refetch]);

  useEffect(() => {
    if (data && data.missing_contacts) {
      setMissingContacts(data.missing_contacts);
    } else {
      setMissingContacts([]);
    }
  }, [data]);

  const flatContactRows = useMemo(() => {
    const rows = [];

    filteredData.forEach((employerId) => {
      const employerData = data.employers[employerId];

      if (showMain && employerData.main_contact) {
        rows.push({
          employerId,
          companyName: employerData.company_name,
          contact: employerData.main_contact,
          type: "Main Contact",
        });
      }
      if (showSecondary && employerData.secondary_contact) {
        rows.push({
          employerId,
          companyName: employerData.company_name,
          contact: employerData.secondary_contact,
          type: "Secondary Contact",
        });
      }
      if (showOther && employerData.other_contacts?.length > 0) {
        employerData.other_contacts.forEach((contact) => {
          if (
            (!hrFilter || contact.hrYN) &&
            (!recruiterFilter || contact.recruiterYN)
          ) {
            rows.push({
              employerId,
              companyName: employerData.company_name,
              contact,
              type: "Other Contact",
            });
          }
        });
      }
    });

    return rows;
  }, [data, filteredData, showMain, showSecondary, showOther, hrFilter, recruiterFilter]);


  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading data.</div>;
  }

  const filterContacts = (type) => {
    if (type.toLowerCase().includes("main") && !showMain) return "hidden";
    if (type.toLowerCase().includes("secondary") && !showSecondary) return "hidden";
    if (type.toLowerCase().includes("other") && !showOther) return "hidden";
    return "";
  };

  const copyToClipboard = () => {
    let tableText = "";
    const headers = ["employer ID", "Company Name", "Contact Type", "First Name", "Last Name", "Job Title", "Email", "Phone"];
    tableText += headers.join("\t") + "\n";

    const rows = [];

    if (data) {
      Object.keys(data).forEach((employerId) => {
        const employerData = data[employerId];
        if (showMain) {
          const mainContact = renderContactText(employerId, employerData.company_name, employerData.main_contact, "Main Contact");
          if (mainContact) rows.push(mainContact);
        }
        if (showSecondary) {
          const secondaryContact = renderContactText(
            employerId,
            employerData.company_name,
            employerData.secondary_contact,
            "Secondary Contact"
          );
          if (secondaryContact) rows.push(secondaryContact);
        }
        if (showOther && employerData.other_contacts) {
          employerData.other_contacts.forEach((contact) => {
            const otherContact = renderContactText(employerId, employerData.company_name, contact, "Other Contact");
            if (otherContact) rows.push(otherContact);
          });
        }
      });
    }

    tableText += rows.join("\n");

    navigator.clipboard
      .writeText(tableText)
      .then(() => {
        alert("Table copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const renderContactText = (employerId, companyName, contact, type) => {
    if (!contact || ((contact.email === null || contact.email === "") && (contact.phone === null || contact.phone === ""))) {
      return "";
    }

    return [
      employerId,
      companyName,
      type,
      contact.firstName || "N/A",
      contact.lastName || "N/A",
      contact.jobtitle || "N/A",
      contact.email || "N/A",
      contact.phone || "N/A",
    ].join("\t");
  };




  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Select a State</h2>
      <select value={selectedState} onChange={handleStateChange} className="mb-4 p-2 border border-gray-300 rounded">
        <option value="">Select a state</option>
        {stateList.map((state) => (
          <option key={state} value={state} style={{ color: missingContacts.length > 0 && selectedState === state ? "red" : "black" }}>
            {state}
          </option>
        ))}
      </select>

      <div className="mb-4 flex gap-4">
        <label className="flex items-center">
          <input type="checkbox" checked={showMain} onChange={() => setShowMain(!showMain)} className="mr-1" />
          Main Contacts
        </label>
        <label className="flex items-center">
          <input type="checkbox" checked={showSecondary} onChange={() => setShowSecondary(!showSecondary)} className="mr-1" />
          Secondary Contacts
        </label>
        <label className="flex items-center">
          <input type="checkbox" checked={showOther} onChange={() => setShowOther(!showOther)} className="mr-1" />
          Other Contacts
        </label>
      </div>

      {showOther && (
        <div className="mb-4">
          <h3>Other Contact Filters:</h3>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hrFilter}
                onChange={() => setHrFilter(!hrFilter)}
                className="mr-1"
              />
              HR
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={recruiterFilter}
                onChange={() => setRecruiterFilter(!recruiterFilter)}
                className="mr-1"
              />
              Recruiter
            </label>
          </div>
        </div>
      )}

      <div className="mb-4">
        <h3 className="font-semibold mb-2">Filter by School Affiliation</h3>
        <div className="flex flex-wrap gap-4">
          {schoolAffiliationOptions.map((option) => (
            <label key={option} className="flex items-center">
              <input
                type="checkbox"
                checked={selectedSchoolAffiliations.includes(option)}
                onChange={() => {
                  setSelectedSchoolAffiliations((prev) =>
                    prev.includes(option)
                      ? prev.filter((v) => v !== option)
                      : [...prev, option]
                  );
                }}
                className="mr-1"
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="font-semibold mb-2">Filter by School Type</h3>
        <div className="flex flex-wrap gap-4">
          {schoolTypeOptions.map((option) => (
            <label key={option} className="flex items-center">
              <input
                type="checkbox"
                checked={selectedSchoolTypes.includes(option)}
                onChange={() => {
                  setSelectedSchoolTypes((prev) =>
                    prev.includes(option)
                      ? prev.filter((v) => v !== option)
                      : [...prev, option]
                  );
                }}
                className="mr-1"
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      {missingContacts.length > 0 && (
        <div className="mb-4 p-4 border border-red-500 bg-red-100 rounded">
          <div className="flex justify-between items-center">
            <h3 className="text-red-600 font-bold">Missing Contacts</h3>
            <button
              onClick={() => setIsMissingContactsVisible(!isMissingContactsVisible)}
              className="text-blue-500 underline"
            >
              {isMissingContactsVisible ? "Hide" : "Show"}
            </button>
          </div>
          {isMissingContactsVisible && (
            <ul className="list-disc pl-5 text-red-700">
              {missingContacts.map((contact) => (
                <li key={contact.employer_id}>
                  <span
                    className="text-gray-600 hover:underline hover:text-blue-500 cursor-pointer"
                    onClick={() => {
                      dispatch(setEmployer({ employer_id: contact.employer_id, company_name: contact.company_name }));
                      navigate(`/employers-center/${contact.employer_id}`);
                    }}
                  >
                    {contact.company_name}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {data?.employers && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Contacts for {selectedState}</h3>
          <button onClick={copyToClipboard} className="mb-4 p-2 bg-blue-500 text-white rounded mr-4">
            Copy to Clipboard
          </button>
          <button
            onClick={() => setShowEmailEditor(!showEmailEditor)}
            className="mb-4 px-4 py-2 bg-green-500 text-white rounded"
          >
            {showEmailEditor ? "Close Email Editor" : "Create Email"}
          </button>

          <div className="p-4">
            {/* Create Email Button */}

            {/* Email Editor (Above Table) */}
            {showEmailEditor && (
              <EmailEditor contacts={filteredContacts} onClose={() => setShowEmailEditor(false)} />
            )}
          </div>
          {/* Table header - preserved as a real table */}
          <div className="grid grid-cols-8 bg-gray-200 text-gray-600 uppercase text-sm font-semibold">
            <div className="py-3 px-4">Employer ID</div>
            <div className="py-3 px-4">Company Name</div>
            <div className="py-3 px-4">Contact Type</div>
            <div className="py-3 px-4">First Name</div>
            <div className="py-3 px-4">Last Name</div>
            <div className="py-3 px-4">Job Title</div>
            <div className="py-3 px-4">Email</div>
            <div className="py-3 px-4">Phone</div>
          </div>

          <List
            height={600}
            itemCount={flatContactRows.length}
            itemSize={50}
            width="100%"
          >
            {({ index, style }) => {
              const row = flatContactRows[index];
              const { employerId, companyName, contact, type } = row;

              if (!contact || (!contact.email && !contact.phone)) return null;

              return (
                <div
                  style={style}
                  className="grid grid-cols-8 border-b text-sm text-gray-700"
                  key={`${employerId}-${type}-${index}`}
                >
                  <div className="p-2">{employerId}</div>
                  <div className="p-2">{companyName}</div>
                  <div className="p-2">{type}</div>
                  <div className="p-2">{contact.firstName || "N/A"}</div>
                  <div className="p-2">{contact.lastName || "N/A"}</div>
                  <div className="p-2">{contact.jobtitle || "N/A"}</div>
                  <div className="p-2">{contact.email || "N/A"}</div>
                  <div className="p-2">{contact.phone || "N/A"}</div>
                </div>
              );
            }}
          </List>

        </div>
      )}
    </div>);
}; export default SalesDatabase;


